import { homeLink, request, resourceLink } from "../../services/cpln";
import { DryRunIssue, DryRunResponse, DryRunSchemaError } from "../../services/utils";
import { v4 as uuidv4 } from "uuid";
import { DomainDryRunConfig } from "../../schema/types/domain/input";

class Mk8sDryRunClass {
  forEdit: boolean = false;
  id: string = uuidv4();
  key: string = uuidv4();
  response: DryRunResponse = null as any;

  reset(forEdit: boolean) {
    this.forEdit = forEdit;
    this.response = null as any;
  }

  setResponse(value: DryRunResponse) {
    this.response = value;
  }

  get hasError() {
    return this.response?.details?.issues?.some((i) => i.severity === "error");
  }

  hasErrorOf(checks: string[]) {
    const filteredIssues = this.response?.details?.issues?.filter((i) => i.severity === "error") || [];
    for (const check of checks) {
      if (filteredIssues.some((i) => i.path.includes(check))) {
        return true;
      }
    }
    return false;
  }

  *run(body: any) {
    try {
      const url = this.forEdit ? resourceLink("mk8s", body.name) : homeLink("mk8s");
      const method = this.forEdit ? "patch" : "post";
      // @ts-ignore
      const res = yield request({ url: url + "?dryRun", body, method: method });
      this.response = processDryRunResponse({ ...res.data, message: res.data.message });
    } catch (e) {
      this.response = processDryRunResponse({ ...e.response.data, message: e.response.data.message });
    }
    this.key = uuidv4();
  }
}

export const mk8sDryRun = new Mk8sDryRunClass();

/*  
{
  "status":"ready",
  "warning":"",
  "dnsConfig":[
    {"ttl":300,"host":"cname","type":"CNAME","value":"<gvcAlias>.t.cpln.app"}
  ],
  "endpoints":[],
  "locations":[],
  "fingerprint":"l9Fw4VUO7kr8CvBlt4zaMCqXZ0w"
}
*/

interface DomainPatchStatus {
  status: string;
  warning: string;
  dnsConfig: DomainDryRunConfig[];
  endpoints: any[];
  locations: any[];
  fingerprint: string;
}
function isDomainPatchStatus(res: DomainPatchStatus) {
  return typeof res.status === "string" && res.fingerprint && res.endpoints && res.locations;
}
function processDomainPatchStatusAsDryRun(res: DomainPatchStatus): DryRunResponse {
  return {
    code: "Domain is OK",
    id: uuidv4(),
    message: "",
    status: 200,
    details: {
      dnsConfig: res.dnsConfig,
    } as any,
  };
}
function isMk8sPatchStatus(res: any) {
  return res.kind === "mk8s";
}
function processMk8sPatchStatusAsDryRun(res: any): DryRunResponse {
  return {
    code: "Kubernetes is OK",
    id: uuidv4(),
    message: "",
    status: 200,
    details: {} as any,
  };
}

export function processDryRunResponse(res: DryRunResponse | DryRunSchemaError): DryRunResponse {
  try {
    if (isDomainPatchStatus(res as any)) {
      return processDomainPatchStatusAsDryRun(res as any);
    }

    if (!isSchemaError(res)) {
      return res as DryRunResponse;
    }

    if (!!res && Array.isArray(res.details)) {
      // Convert from array of details
      const schemaErr = res as DryRunSchemaError;

      if (!schemaErr || !schemaErr.details) return null as any;

      let issues: DryRunIssue[] = [];

      issues = schemaErr.details.map((d) => {
        let p = "";

        for (let pathIndexStr in d.path) {
          const pathIndex = Number(pathIndexStr);
          const isFirst = pathIndex === 0;
          const pathPart = d.path[pathIndex];
          const isNumber = !Number.isNaN(Number(pathPart));
          if (isNumber) {
            p += `[${pathPart}]`;
          } else {
            p += `${isFirst ? "" : "."}${pathPart}`;
          }
        }

        return {
          message: d.message || "",
          path: p,
          severity: "error",
        };
      });

      return {
        code: "400",
        details: { issues: issues },
        id: schemaErr.id,
        message: schemaErr.message,
        status: schemaErr.status,
      };
    }

    if (isMk8sPatchStatus(res)) {
      return processMk8sPatchStatusAsDryRun(res);
    }

    // should not hit this
    console.log("should not hit this @processDryRunResponse");
    return res as any;
  } catch (e) {
    console.error("Error processing mk8s dry run: ", e.message);
    return null as any;
  }
}

function isSchemaError(res: DryRunResponse | DryRunSchemaError) {
  return !((res as unknown) as DryRunResponse).details?.issues;
}
